import { LocalApiError, type LocalApiClient, type LocalSnapshot } from "./local-api.ts";

export type UploadProgress = "sending" | "accepted" | "sent" | "replied" | "unconfirmed" | "failed" | "cancelled";
type Options = {
  isCurrent: () => boolean;
  pause: (ms: number) => Promise<void>;
  onUpdate: (snapshot: LocalSnapshot, progress: UploadProgress) => void;
  maxChecks?: number;
  readSnapshot?: (deviceId: string) => Promise<LocalSnapshot>;
};

export async function followUploadRequest(client: LocalApiClient, deviceId: string, intervalSeconds: number, options: Options) {
  const readSnapshot = options.readSnapshot ?? ((id: string) => client.snapshot(id));
  const check = () => { if (!options.isCurrent()) throw new LocalApiError("cancelled"); };
  const baseline = await readSnapshot(deviceId);
  check();
  if (!baseline.device?.connected) throw new LocalApiError("deviceOffline");
  if (!baseline.device.capabilities?.uploadInterval) throw new LocalApiError("featureUnavailable");
  const { commandId } = await client.setUploadInterval(deviceId, intervalSeconds);
  check();
  options.onUpdate(baseline, "accepted");
  let latest = baseline;
  for (let i = 0; i < (options.maxChecks ?? 10); i++) {
    await options.pause(3000);
    check();
    latest = await readSnapshot(deviceId);
    check();
    const command = latest.device?.uploadCommand;
    if (command?.id === commandId) {
      if (command.repliedAt) { options.onUpdate(latest, "replied"); return; }
      if (command.status === "failed") { options.onUpdate(latest, "failed"); return; }
      options.onUpdate(latest, command.sentAt ? "sent" : "accepted");
    }
    if (!latest.device?.connected) break;
  }
  options.onUpdate(latest, "unconfirmed");
}
